import { upsertPublicClient } from '../../../api/companyRecords';
import {
  addStorefrontCompanyAccessMe,
  getStorefrontGlobalUserMeOrNull,
  grantStorefrontCompanyAccess,
  lookupStorefrontGlobalUser,
  patchStorefrontGlobalUserProfile,
  recordStorefrontLastLogin,
  upsertStorefrontGlobalUser,
} from '../../../api/storefront/globalUsers';
import { tryGetApiToken } from '../../../api/authToken';

export class GlobalUserService {
  static async createOrUpdateGlobalUser(userData, companyId) {
    return upsertStorefrontGlobalUser({
      companyId,
      user: {
        uid: userData.uid,
        email: userData.email,
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
        phone: userData.phone || '',
        smsConsent: userData.smsConsent || false,
        isClientOnly: userData.isClientOnly || false,
        isSpecialAdmin: userData.isSpecialAdmin || false,
      },
    });
  }

  static async getGlobalUser(userId) {
    const token = await tryGetApiToken();
    if (!token) return null;
    const user = await getStorefrontGlobalUserMeOrNull({ token });
    if (!user) return null;
    if (userId && user.id !== userId && user.uid !== userId) return null;
    return user;
  }

  static async userHasCompanyAccess(userId, companyId) {
    try {
      const user = await this.getGlobalUser(userId);
      if (user) {
        return Boolean(user.companies && user.companies.includes(companyId));
      }
      return false;
    } catch (error) {
      console.error('Error checking company access:', error);
      return false;
    }
  }

  static async addCompanyAccess(userId, companyId, email) {
    const self = await addStorefrontCompanyAccessMe({ companyId });
    if (self) return self;
    const result = await grantStorefrontCompanyAccess({
      userId,
      email,
      companyId,
    });
    return result.user;
  }

  static async removeCompanyAccess() {
    return null;
  }

  static async updateLastLogin(userId, companyId) {
    try {
      await recordStorefrontLastLogin({ companyId });
      return true;
    } catch (error) {
      if (error?.status === 403 || error?.status === 401) {
        console.warn('Skipping global last-login update.', {
          userId,
          companyId,
          status: error?.status,
          message: error?.message,
        });
        return false;
      }
      console.error('Error updating last login:', error);
      throw error;
    }
  }

  static async getUserCompanyProfile(userId, companyId) {
    const userData = await this.getGlobalUser(userId);
    if (!userData) return null;
    const companyProfile = userData.companyProfiles?.[companyId] || {};
    return {
      ...userData,
      ...companyProfile,
    };
  }

  static async findUserByEmail(email, companyId) {
    if (!email) return null;
    const result = await lookupStorefrontGlobalUser({
      email: String(email).toLowerCase(),
      companyId,
    });
    if (!result?.found) return null;
    return {
      id: result.id,
      uid: result.uid || result.id,
      accountType: result.accountType,
      hasCompanyAccess: Boolean(result.hasCompanyAccess),
    };
  }

  static async updateCompanyProfile(companyId, profile) {
    return patchStorefrontGlobalUserProfile({ companyId, profile });
  }

  static async syncCompanyClientData(userId, companyId, clientData) {
    const now = new Date().toISOString();
    const payload = {
      ...clientData,
      globalUserId: userId,
      lastSync: now,
      updatedAt: now,
    };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) delete payload[key];
    });
    await upsertPublicClient({
      companyId,
      id: userId,
      record: payload,
    });
  }
}
